import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  Award, Sparkles, Heart, Leaf, ArrowRight, Quote,
  Scissors, Palette, Star, GraduationCap
} from "lucide-react";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import {
  TAMARA_PORTRAIT,
  MIRROR_CLIENT,
  SALON_INTERIOR_COTRIL,
  SOBRE_HERO,
} from "@/data/gallery";

const Counter = ({ end, suffix = "", label }) => {
  const ref = useRef(null);
  const [n, setN] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const duration = 1800;
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min((t - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(end * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div ref={ref} className="text-center">
      <p className="font-serif text-6xl lg:text-7xl gold-text leading-none tabular-nums">
        {n}{suffix}
      </p>
      <p className="overline mt-3 !text-ink/70">{label}</p>
    </div>
  );
};

const journey = [
  {
    year: "2018",
    icon: GraduationCap,
    title: "Academia Lucia Piloto",
    text: "Formação de excelência em técnicas avançadas de coloração e estética capilar.",
  },
  {
    year: "2020",
    icon: Palette,
    title: "Especialização em Cor",
    text: "Foco profundo em Balayage, morena iluminada e técnicas livres de amoníaco.",
  },
  {
    year: "2022",
    icon: Sparkles,
    title: "Abertura do Estúdio",
    text: "Estúdio próprio em Venda do Pinheiro. Espaço pensado para o detalhe e o conforto.",
  },
  {
    year: "Hoje",
    icon: Award,
    title: "Referência regional",
    text: "Avaliação 5★ no Google. Centenas de clientes fiéis. Formação contínua.",
  },
];

const values = [
  {
    icon: Award,
    title: "Excelência técnica",
    text: "Formação contínua e técnica refinada para resultados sempre ao mais alto nível.",
  },
  {
    icon: Heart,
    title: "Atenção pessoal",
    text: "Cada cliente é única. Cada sessão é desenhada à medida da sua personalidade.",
  },
  {
    icon: Sparkles,
    title: "Beleza natural",
    text: "Realçar o que já é seu — com cor, luz e movimento que parecem nascer da pele.",
  },
  {
    icon: Leaf,
    title: "Cuidado capilar",
    text: "Produtos profissionais Cotril que cuidam, fortalecem e prolongam o resultado.",
  },
];

const Sobre = () => {
  return (
    <div data-testid="sobre-page" className="pb-24 overflow-x-clip">
      {/* ===== CINEMATIC HERO ===== */}
      <section className="relative min-h-[92vh] flex items-center pt-8">
        {/* Background giant letter */}
        <span
          aria-hidden
          className="absolute -top-20 -left-32 font-serif italic text-[40rem] leading-none text-gold/[0.06] select-none pointer-events-none"
        >
          A
        </span>

        <div className="container-luxe relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          {/* Text side */}
          <Reveal className="lg:col-span-6">
            <p className="overline mb-6" data-testid="sobre-overline">— A Hairstylist</p>
            <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-[0.95] text-ink">
              Tamara<br />
              <span className="italic gold-text">Alves</span>
            </h1>
            <p className="mt-8 font-serif text-2xl text-ink/80 italic leading-snug max-w-md">
              "Acredito que cada cabelo conta uma história — e o meu trabalho é fazer com que essa história brilhe."
            </p>
            <div className="mt-10 flex items-center gap-6">
              <div className="flex gap-0.5 text-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-ink/60">5★ Google · 4.976 reviews</span>
            </div>
          </Reveal>

          {/* Image side — tilted 3D card */}
          <Reveal className="lg:col-span-6 perspective-1000">
            <TiltCard maxTilt={6} className="relative">
              <div className="relative img-frame-3d">
                <img
                  src={SOBRE_HERO}
                  alt="Tamara Alves no seu estúdio"
                  className="w-full aspect-[4/5] object-cover glow-gold"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-8 -left-8 bg-gold-bright text-ink p-5 shadow-[0_20px_60px_-10px_rgba(218,165,32,0.5)] float-3d">
                  <p className="overline mb-1 !text-ink">— Estúdio próprio</p>
                  <p className="font-serif text-lg leading-tight italic">Venda do Pinheiro</p>
                </div>
                <div className="absolute -top-6 -right-6 bg-ink text-beige p-5 shadow-2xl float-3d-delayed">
                  <p className="font-serif text-3xl gold-text leading-none">7+</p>
                  <p className="overline mt-1 !text-gold">— anos</p>
                </div>
              </div>
            </TiltCard>
          </Reveal>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/40">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <span className="w-px h-12 bg-gradient-to-b from-gold to-transparent animate-pulse" />
        </div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <section className="bg-ink text-beige border-y border-gold/30 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold/5 to-transparent" />
        <div className="container-luxe py-16 grid grid-cols-2 lg:grid-cols-4 gap-10 relative">
          <Counter end={500} suffix="+" label="Clientes felizes" />
          <Counter end={7} suffix="+" label="Anos de experiência" />
          <Counter end={4976} label="Reviews 5 estrelas" />
          <Counter end={15} label="Produtos Cotril em stock" />
        </div>
      </section>

      {/* ===== BIO + SECOND IMAGE ===== */}
      <section className="container-luxe py-32 grid lg:grid-cols-12 gap-16 items-center">
        <Reveal className="lg:col-span-5 perspective-1000">
          <TiltCard maxTilt={5}>
            <img
              src={MIRROR_CLIENT}
              alt="Tamara com cliente no espelho do estúdio"
              className="w-full aspect-[3/4] object-cover glow-gold"
            />
          </TiltCard>
        </Reveal>

        <Reveal className="lg:col-span-7 space-y-6 text-ink/80 leading-relaxed">
          <p className="overline">— A minha história</p>
          <h2 className="font-serif text-5xl lg:text-6xl text-ink font-light leading-tight">
            Da paixão à <span className="italic gold-text">vocação</span>.
          </h2>
          <p className="text-lg">
            Comecei a minha viagem na <span className="text-gold font-medium">Academia Lucia Piloto</span>,
            onde mergulhei numa visão profissional, técnica e estética do cabelo. Foi aí que
            descobri a minha verdadeira paixão: a coloração — e, sobretudo, o Balayage.
          </p>
          <p className="text-lg">
            Hoje, no meu estúdio em Venda do Pinheiro, recebo cada cliente como uma pequena
            missão. Ouvir. Perceber. Criar. Trabalho de forma artesanal, com atenção ao detalhe
            e total respeito pela saúde do cabelo.
          </p>
          <p className="text-lg">
            Especializei-me em <em className="text-gold not-italic font-medium">Balayage, morena iluminada
            e técnicas avançadas de coloração</em>, e utilizo exclusivamente produtos profissionais
            da linha Cotril — pelo seu rigor, performance e cuidado capilar.
          </p>
          <div className="pt-4 flex items-center gap-3 text-sm text-ink/60">
            <Scissors className="w-4 h-4 text-gold" strokeWidth={1.5} />
            <span>Hairstylist · Coloração · Balayage · Estética</span>
          </div>
        </Reveal>
      </section>

      {/* ===== JOURNEY TIMELINE ===== */}
      <section className="bg-beige-light border-y border-gold/15 py-32">
        <div className="container-luxe">
          <Reveal className="max-w-2xl mb-20">
            <p className="overline mb-4">— O percurso</p>
            <h2 className="font-serif text-5xl lg:text-6xl font-light text-ink leading-tight">
              Sete anos a desenhar <span className="italic gold-text">cabelos.</span>
            </h2>
          </Reveal>

          <div className="relative">
            {/* Vertical gold line */}
            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold to-transparent" />

            <div className="space-y-16 lg:space-y-32">
              {journey.map((step, idx) => {
                const Icon = step.icon;
                const left = idx % 2 === 0;
                return (
                  <Reveal key={step.year} className="relative">
                    <div className={`grid lg:grid-cols-2 gap-8 items-center`}>
                      <div className={`${left ? "lg:order-1 lg:text-right lg:pr-16" : "lg:order-2 lg:pl-16"}`}>
                        <p className="font-serif text-6xl lg:text-8xl gold-text leading-none">{step.year}</p>
                        <h3 className="font-serif text-2xl text-ink mt-3">{step.title}</h3>
                        <p className="text-ink/70 mt-3 max-w-sm lg:max-w-none">{step.text}</p>
                      </div>
                      <div className={`${left ? "lg:order-2" : "lg:order-1"} hidden lg:block`} />
                    </div>

                    {/* Dot on timeline */}
                    <div className="absolute left-4 lg:left-1/2 top-4 -translate-x-1/2 w-3 h-3 bg-gold-bright shadow-[0_0_20px_rgba(255,215,0,0.6)] rounded-full ring-4 ring-beige-light">
                      <Icon className="hidden" />
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== QUOTE / PHILOSOPHY ===== */}
      <section className="container-luxe py-32">
        <Reveal className="max-w-4xl mx-auto text-center">
          <Quote className="w-16 h-16 text-gold/30 mx-auto mb-8" strokeWidth={0.75} />
          <p className="font-serif text-4xl sm:text-5xl lg:text-6xl italic text-ink leading-[1.15] font-light">
            "Sair do meu estúdio deve ser muito mais do que ter um cabelo bonito. Deve ser um momento de{" "}
            <span className="gold-text not-italic font-normal">confiança e leveza</span> — de
            celebração de quem somos."
          </p>
          <p className="overline mt-12">— Tamara Alves</p>
        </Reveal>
      </section>

      {/* ===== STUDIO BACKGROUND ===== */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={SALON_INTERIOR_COTRIL}
          alt="Interior do estúdio com produtos Cotril"
          className="absolute inset-0 w-full h-full object-cover ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="container-luxe">
            <Reveal className="max-w-xl text-beige">
              <p className="overline !text-gold mb-4">— O Estúdio</p>
              <h2 className="font-serif text-5xl lg:text-6xl font-light leading-tight">
                Um espaço pensado <br />
                para o <span className="italic gold-text">detalhe.</span>
              </h2>
              <p className="mt-6 text-beige/80 text-lg leading-relaxed">
                Pequeno e exclusivo, com atendimento personalizado e a gama profissional Cotril
                ao seu dispor. Cada visita é uma experiência.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== VALUES ===== */}
      <section className="container-luxe py-32" data-testid="sobre-values">
        <Reveal className="max-w-2xl mb-20">
          <p className="overline mb-4">— Os meus valores</p>
          <h2 className="font-serif text-5xl lg:text-6xl font-light text-ink">
            Compromisso com <span className="italic gold-text">cada detalhe</span>.
          </h2>
        </Reveal>

        <Reveal stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <TiltCard key={i} maxTilt={6} className="bg-beige-light p-10 group">
                <div data-testid={`value-${i}`}>
                  <div className="w-14 h-14 border border-gold/30 flex items-center justify-center mb-6 group-hover:bg-gold group-hover:border-gold transition-colors duration-500">
                    <Icon className="w-6 h-6 text-gold group-hover:text-ink transition-colors" strokeWidth={1.25} />
                  </div>
                  <h3 className="font-serif text-2xl text-ink mb-3">{v.title}</h3>
                  <p className="text-sm text-ink/65 leading-relaxed">{v.text}</p>
                </div>
              </TiltCard>
            );
          })}
        </Reveal>
      </section>

      {/* ===== CTA ===== */}
      <section className="container-luxe">
        <Reveal>
          <div className="bg-ink text-beige relative overflow-hidden grid lg:grid-cols-12 gap-0">
            <div className="lg:col-span-5 relative min-h-[300px] lg:min-h-[420px]">
              <img
                src={SOBRE_HERO}
                alt="Tamara Alves"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink lg:to-ink/40" />
            </div>
            <div className="lg:col-span-7 p-12 lg:p-16 relative flex flex-col justify-center">
              <span aria-hidden className="absolute -top-20 -right-20 font-serif italic text-[20rem] leading-none text-gold/10 select-none pointer-events-none">A</span>
              <p className="overline !text-gold mb-4 relative">— Pronta para começar?</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight relative">
                Vamos criar <span className="italic gold-text">algo seu</span>.
              </h2>
              <p className="mt-6 text-beige/70 max-w-md relative">
                Marque a sua primeira sessão e descubra a diferença de um trabalho artesanal,
                pensado ao detalhe e feito com paixão.
              </p>
              <div className="mt-10 relative">
                <Link to="/agendamentos" data-testid="sobre-cta-btn" className="btn-gold inline-flex">
                  Marcar a primeira sessão <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Sobre;
